import { useRef, useState } from 'react';
import {
  Paperclip,
  Image,
  MessageSquare,
  Send,
  AlertTriangle,
  Info,
  X,
  Loader2,
  FileText,
  Settings2,
  Mail,
  Users,
} from 'lucide-react';
import { LIMITS } from '@superchat/shared/constants';
import { uploadFile, formatBytes, isImage } from '../lib/upload.js';
import { useShortcuts, matchShortcuts } from '../lib/shortcuts.jsx';
import ShortcutsDialog from './ShortcutsDialog.jsx';

let uploadSeq = 0;

/** Shown on the toggle and in its notice, so both say the same thing. */
const USER_ALERT_HINT = 'Forces the chat open on the visitor’s screen.';

/**
 * Conversations where the agent asked not to be prompted again.
 *
 * Deliberately module-scoped rather than persisted: "don't ask again" means
 * for as long as I am working this thread, not forever. A preference stored in
 * localStorage would still be suppressing the dialog months later, on a thread
 * whose visitor has since left an address — and the failure mode there is a
 * reply nobody is ever told about.
 */
const skipOfflinePrompt = new Set();

/**
 * "This visitor is offline right now."
 *
 * Sits between the composer and the send so that Cancel can leave the draft
 * exactly where it was — the message has not been cleared, or sent, or
 * optimistically rendered at the point this is on screen.
 */
function OfflineSendDialog({ visitorName, visitorEmail, canEmail, onChoose, onCancel }) {
  const [remember, setRemember] = useState(false);

  const emailReason = !visitorEmail
    ? 'No email address on record for this visitor.'
    : !canEmail
      ? 'This app has no email sender connected. Add one in Integration → Customer email.'
      : `Goes to ${visitorEmail}.`;

  return (
    <div className="dialog-backdrop" onMouseDown={onCancel}>
      <div
        className="dialog dialog--narrow"
        role="dialog"
        aria-modal="true"
        aria-label="Visitor is offline"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="dialog__title">{visitorName} is offline right now</h2>
        <p className="dialog__lede">
          Your reply will be waiting in the thread whenever they come back. You can also let
          them know it is there.
        </p>

        <label className="dialog__check">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          Don’t ask again for this conversation
        </label>

        <div className="dialog__actions dialog__actions--stack">
          <button
            type="button"
            className="button button--primary"
            disabled={!canEmail || !visitorEmail}
            title={emailReason}
            onClick={() => onChoose({ notifyByEmail: true, remember })}
          >
            <Mail size={13} />
            Send and notify by email
          </button>
          <span className="dialog__note">{emailReason}</span>

          <button
            type="button"
            className="button"
            onClick={() => onChoose({ notifyByEmail: false, remember })}
          >
            Send only
          </button>
          <button type="button" className="button button--ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Two agents, one thread.
 *
 * Raised in the same place and for the same reason as the offline dialog above:
 * before anything is cleared, sent or rendered, so declining costs the agent
 * nothing — the draft, the attachments and the alert toggle are all still where
 * they left them.
 *
 * Deliberately not a lock. Two people working one incident together is a real
 * thing, and the second of them is one click from replying. What this prevents
 * is the accidental case: an agent who did not notice a colleague was already
 * mid-answer, and a visitor who gets told the same thing twice by two people.
 */
function ContentionDialog({ contention, visitorName, onConfirm, onCancel }) {
  return (
    <div className="dialog-backdrop" onMouseDown={onCancel}>
      <div
        className="dialog dialog--narrow"
        role="dialog"
        aria-modal="true"
        aria-label="Another agent is in this conversation"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="dialog__title">
          {contention.typingName
            ? `${contention.typingName} is replying right now`
            : `${contention.holderName} is already in this conversation`}
        </h2>
        <p className="dialog__lede">
          {contention.typingName
            ? 'They are typing an answer as you read this.'
            : `They opened it before you did, so it is theirs to answer.`}{' '}
          Send yours as well and {visitorName} may get the same question answered twice.
        </p>

        <div className="dialog__actions dialog__actions--stack">
          <button type="button" className="button button--primary" onClick={onConfirm}>
            <Users size={13} />
            Send it anyway
          </button>
          <button type="button" className="button button--ghost" onClick={onCancel}>
            Hold off — let them answer
          </button>
        </div>
      </div>
    </div>
  );
}

export function Composer({
  onSend,
  onTyping,
  sending,
  visitorName,
  appId,
  conversationId,
  uploads = { enabled: false, maxBytes: 0 },
  // Whether the visitor is on the page right now, whether this app can email
  // them at all, and where to. All three decide what the offline dialog can
  // offer — see `submit`.
  visitorOffline = false,
  visitorEmail = '',
  canEmailVisitor = false,
  /**
   * `{ holderName, typingName }` when a colleague got to this thread first,
   * null when nobody did — which is the ordinary case and asks nothing.
   */
  contention = null,
}) {
  const [value, setValue] = useState('');
  const [emergency, setEmergency] = useState(false);
  const [confirmingOffline, setConfirmingOffline] = useState(false);
  const [confirmingContention, setConfirmingContention] = useState(false);
  const [mode, setMode] = useState('reply');
  // One entry per file the agent has picked, from the moment it is picked:
  // an upload in flight is visible, cancellable and does not block typing.
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [editingShortcuts, setEditingShortcuts] = useState(false);
  // Which entry of the `/` menu is selected. Reset whenever the query changes.
  const [highlight, setHighlight] = useState(0);
  const { shortcuts } = useShortcuts();
  const box = useRef(null);
  const filePicker = useRef(null);
  const imagePicker = useRef(null);
  const typing = useRef(false);

  const signalTyping = (isTyping) => {
    if (typing.current === isTyping) return;
    typing.current = isTyping;
    onTyping(isTyping);
  };

  const grow = () => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  };

  /**
   * Inserting a shortcut replaces a `/query` in progress and appends
   * otherwise — clicking a chip mid-sentence should add to what is written,
   * but picking one out of the `/` menu is finishing what was typed.
   */
  const applyShortcut = (shortcut) => {
    setValue((current) =>
      matchShortcuts(shortcuts, current).query !== null
        ? shortcut.text
        : current
          ? `${current.trimEnd()} ${shortcut.text}`
          : shortcut.text
    );
    setHighlight(0);
    box.current?.focus();
    requestAnimationFrame(grow);
  };

  /* ----------------------------------------------------------- attachments */

  const attach = (picked) => {
    const list = Array.from(picked ?? []).filter(Boolean);
    if (!list.length || !uploads.enabled || !conversationId) return;

    for (const file of list) {
      const id = `u_${(uploadSeq += 1)}`;

      if (uploads.maxBytes && file.size > uploads.maxBytes) {
        setFiles((current) => [
          ...current,
          {
            id,
            name: file.name,
            size: file.size,
            mimeType: file.type,
            error: `Too large — the limit is ${formatBytes(uploads.maxBytes)}`,
          },
        ]);
        continue;
      }

      setFiles((current) => [
        ...current,
        { id, name: file.name, size: file.size, mimeType: file.type, progress: 0 },
      ]);

      uploadFile(appId, file, {
        conversationId,
        onProgress: (fraction) =>
          setFiles((current) =>
            current.map((f) => (f.id === id ? { ...f, progress: fraction } : f))
          ),
      })
        .then((uploaded) =>
          setFiles((current) =>
            current.map((f) => (f.id === id ? { ...f, ...uploaded, progress: 1 } : f))
          )
        )
        .catch((err) =>
          setFiles((current) =>
            current.map((f) => (f.id === id ? { ...f, error: err.message, progress: undefined } : f))
          )
        );
    }
  };

  const drop = (event) => {
    event.preventDefault();
    setDragging(false);
    attach(event.dataTransfer?.files);
  };

  // A pasted screenshot is the fastest path from "look at this" to sending it.
  const paste = (event) => {
    const pasted = Array.from(event.clipboardData?.files ?? []);
    if (!pasted.length) return;
    event.preventDefault();
    attach(pasted);
  };

  const ready = files.filter((f) => f.key);
  const uploading = files.some((f) => !f.key && !f.error);

  /* ----------------------------------------------------------------- send */

  /** Hands the message off and resets the box. */
  const dispatchSend = ({ notifyByEmail = false } = {}) => {
    const body = value.trim();
    // Expand a lone `/shortcut` before sending, for anyone who typed it in
    // full and pressed Enter without looking at the menu.
    const exact = shortcuts.find((s) => body === `/${s.key}`);
    onSend({
      body: exact ? exact.text : body,
      isEmergency: emergency,
      notifyByEmail,
      attachments: ready.map(({ key, name, mimeType, size }) => ({ key, name, mimeType, size })),
    });

    setValue('');
    setFiles([]);
    setEmergency(false);
    signalTyping(false);
    requestAnimationFrame(grow);
  };

  const submit = ({ ignoreContention = false } = {}) => {
    const body = value.trim();
    if (sending || uploading || mode === 'note') return;
    // A bare attachment is a message; a bare nothing is not.
    if (!body && ready.length === 0) return;

    /**
     * Somebody else got here first.
     *
     * Checked before the offline prompt so the two never stack: whether the
     * visitor is on the page is a question about delivery, and this one is
     * about whether to send at all. A user alert skips it for the same reason
     * it skips the offline prompt — forcing the chat open is never accidental.
     */
    if (contention && !ignoreContention && !emergency) {
      setConfirmingContention(true);
      return;
    }

    /**
     * Nobody is there to read this yet.
     *
     * The dialog goes up *before* anything is cleared or rendered, so Cancel
     * is a true no-op: the draft, the attachments and the alert toggle are all
     * still exactly as the agent left them. A user alert skips the prompt —
     * forcing the chat open on someone who is not there has already been
     * chosen deliberately, and there is nothing to force open.
     */
    if (visitorOffline && !emergency && !skipOfflinePrompt.has(conversationId)) {
      setConfirmingOffline(true);
      return;
    }

    dispatchSend();
  };

  const disabled =
    sending || uploading || mode === 'note' || (!value.trim() && ready.length === 0);

  /* The `/` menu, open only while the box holds a lone `/query`. */
  const { query, matches } = matchShortcuts(shortcuts, value);
  const menuOpen = query !== null && matches.length > 0;
  const selected = matches[Math.min(highlight, matches.length - 1)];

  return (
    <div className="composer-wrap">
      <div className="macros">
        {shortcuts.map((shortcut) => (
          <button
            key={shortcut.id}
            type="button"
            className="macro"
            onClick={() => applyShortcut(shortcut)}
            title={shortcut.text}
          >
            <span className="macro__slash">/</span>
            {shortcut.key}
          </button>
        ))}
        <button
          type="button"
          className="macro macro--manage"
          onClick={() => setEditingShortcuts(true)}
          title="Add, edit or remove shortcuts"
        >
          <Settings2 size={11} />
          {shortcuts.length === 0 ? 'Add a shortcut' : 'Edit'}
        </button>
      </div>

      <div
        className={`composer${emergency ? ' is-emergency' : ''}${dragging ? ' is-dropping' : ''}`}
        onDragOver={(e) => {
          if (!uploads.enabled) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={drop}
      >
        {dragging && (
          <div className="composer__drop">
            <Paperclip size={13} /> Drop to attach
          </div>
        )}

        {emergency && (
          <div className="composer__notice">
            <AlertTriangle size={14} />
            <span>
              This will force the chat open on {visitorName}'s screen. To them it reads as an
              ordinary reply — the alert is on your side.
            </span>
          </div>
        )}

        <div className="composer__modes">
          <button
            type="button"
            className={`mode${mode === 'reply' ? ' is-active' : ''}`}
            onClick={() => setMode('reply')}
          >
            Reply
          </button>
          <button
            type="button"
            className={`mode${mode === 'note' ? ' is-active' : ''}`}
            onClick={() => setMode('note')}
          >
            Internal note
          </button>
        </div>

        {mode === 'note' && (
          <div className="composer__unavailable">
            <Info size={13} />
            <span>
              Internal notes need a visibility field on the message model — every message the
              API stores today is readable by the merchant through <code>GET /widget/messages</code>.
              Sending is disabled here rather than leaking a note to the merchant.
            </span>
          </div>
        )}

        {files.length > 0 && (
          <ul className="attach-tray">
            {files.map((file) => (
              <li
                key={file.id}
                className={`attach-chip${file.error ? ' is-failed' : ''}${
                  file.key ? ' is-ready' : ''
                }`}
                title={file.error || file.name}
              >
                <span className="attach-chip__icon">
                  {file.error ? (
                    <AlertTriangle size={12} />
                  ) : file.key ? (
                    isImage(file.mimeType) ? (
                      <Image size={12} />
                    ) : (
                      <FileText size={12} />
                    )
                  ) : (
                    <Loader2 size={12} className="spin" />
                  )}
                </span>
                <span className="attach-chip__name">{file.name}</span>
                <span className="attach-chip__meta">
                  {file.error
                    ? file.error
                    : file.key
                      ? formatBytes(file.size)
                      : `${Math.round((file.progress ?? 0) * 100)}%`}
                </span>
                {/* The bar is the only honest signal on a slow connection;
                    it sits under the chip rather than replacing the name. */}
                {!file.key && !file.error && (
                  <span
                    className="attach-chip__bar"
                    style={{ transform: `scaleX(${file.progress ?? 0})` }}
                  />
                )}
                <button
                  type="button"
                  className="attach-chip__remove"
                  onClick={() => setFiles((current) => current.filter((f) => f.id !== file.id))}
                  aria-label={`Remove ${file.name}`}
                >
                  <X size={11} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Typing `/` offers what it could become. Arrow keys move, Enter or
            Tab inserts, Escape gets out of the way and leaves the text alone. */}
        {menuOpen && (
          <ul className="slash-menu" role="listbox" aria-label="Shortcuts">
            {matches.map((shortcut, index) => (
              <li key={shortcut.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={shortcut.id === selected?.id}
                  className={`slash-menu__item${
                    shortcut.id === selected?.id ? ' is-active' : ''
                  }`}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => applyShortcut(shortcut)}
                >
                  <span className="slash-menu__key mono">/{shortcut.key}</span>
                  <span className="slash-menu__text">{shortcut.text}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <textarea
          ref={box}
          className="composer__input"
          onPaste={paste}
          rows={2}
          maxLength={LIMITS.MESSAGE_BODY_MAX}
          placeholder={`Reply to ${visitorName}…`}
          aria-label="Message"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setHighlight(0);
            signalTyping(e.target.value.length > 0);
            grow();
          }}
          onBlur={() => signalTyping(false)}
          onKeyDown={(e) => {
            if (menuOpen) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((current) => (current + 1) % matches.length);
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((current) => (current - 1 + matches.length) % matches.length);
                return;
              }
              if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
                e.preventDefault();
                if (selected) applyShortcut(selected);
                return;
              }
              if (e.key === 'Escape') {
                // Closing without inserting: the typed `/query` stays, and a
                // space is what tells the matcher this is prose now.
                e.preventDefault();
                setValue((current) => `${current} `);
                return;
              }
            }

            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />

        <div className="composer__bar">
          <input
            ref={filePicker}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              attach(e.target.files);
              // Reset, or picking the same file twice in a row is a no-op.
              e.target.value = '';
            }}
          />
          <input
            ref={imagePicker}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => {
              attach(e.target.files);
              e.target.value = '';
            }}
          />

          <button
            type="button"
            className="composer__icon"
            onClick={() => filePicker.current?.click()}
            disabled={!uploads.enabled}
            title={
              uploads.enabled
                ? `Attach a file — up to ${formatBytes(uploads.maxBytes)}. Drag one in, or paste it.` +
                  (uploads.retentionDays
                    ? ` Files are deleted after ${uploads.retentionDays} days unless attached to a ticket.`
                    : '')
                : 'File storage is not configured on this server'
            }
            aria-label="Attach a file"
          >
            <Paperclip size={16} />
          </button>
          <button
            type="button"
            className="composer__icon"
            onClick={() => imagePicker.current?.click()}
            disabled={!uploads.enabled}
            title={uploads.enabled ? 'Attach an image' : 'File storage is not configured'}
            aria-label="Attach an image"
          >
            <Image size={16} />
          </button>
          <button
            type="button"
            className="composer__icon"
            onClick={() => setEditingShortcuts(true)}
            title="Manage the /shortcuts you can type here"
            aria-label="Manage shortcuts"
          >
            <MessageSquare size={16} />
          </button>

          <button
            type="button"
            className={`emergency-toggle${emergency ? ' is-on' : ''}`}
            onClick={() => setEmergency((v) => !v)}
            aria-pressed={emergency}
            title={USER_ALERT_HINT}
          >
            <span className="emergency-toggle__track">
              <span className="emergency-toggle__knob" />
            </span>
            User Alert
          </button>

          <span className="composer__hint">⇧↵ newline</span>

          <button
            type="button"
            className={`send-button${emergency ? ' is-emergency' : ''}`}
            onClick={submit}
            disabled={disabled}
          >
            {emergency ? 'Send user alert' : 'Send'}
            <Send size={13} />
          </button>
        </div>
      </div>

      {editingShortcuts && <ShortcutsDialog onClose={() => setEditingShortcuts(false)} />}

      {confirmingOffline && (
        <OfflineSendDialog
          visitorName={visitorName}
          visitorEmail={visitorEmail}
          canEmail={canEmailVisitor}
          onCancel={() => setConfirmingOffline(false)}
          onChoose={({ notifyByEmail, remember }) => {
            if (remember) skipOfflinePrompt.add(conversationId);
            setConfirmingOffline(false);
            dispatchSend({ notifyByEmail });
          }}
        />
      )}

      {confirmingContention && contention && (
        <ContentionDialog
          contention={contention}
          visitorName={visitorName}
          onCancel={() => setConfirmingContention(false)}
          onConfirm={() => {
            setConfirmingContention(false);
            // Straight back through submit rather than to dispatchSend, so an
            // offline visitor still gets the email question afterwards.
            submit({ ignoreContention: true });
          }}
        />
      )}
    </div>
  );
}

export default Composer;
